<template>
  <div id="bank-update-dialog">
    <!-- Professional Header -->
    <v-card elevation="12" class="header-card mb-0 overflow-hidden">
      <div class="header-gradient">
        <v-container fluid class="header-container">
          <v-row align="center" justify="space-between" class="ma-0">
            <!-- Title Section - Left -->
            <v-col cols="12" md="8" class="d-flex align-center gap-3">
              <div class="header-icon-box">
                <v-icon size="28" class="icon-white">mdi-bank</v-icon>
              </div>
              <div class="header-title-bar">
                <h2 class="header-title">Update Bank Details</h2>
                <p class="header-subtitle">Add or modify bank account information</p>
              </div>
            </v-col>

            <!-- Action Buttons - Right -->
            <v-col cols="12" md="4" class="d-flex justify-end gap-2">
              <v-btn icon small color="white" class="header-action-btn" @click="refreshPageData">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
              <v-btn icon small color="white" class="header-action-btn" @click="closePrompt">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <!-- Form Card -->
    <v-card class="form-card" elevation="8">
      <v-form
        ref="form2"
        v-model="valid1"
        lazy-validation
        @submit.prevent="BankDetailsUpdate"
      >
        <v-card-text class="form-content">
          <!-- Required Fields Notice -->
          <div class="required-notice">
            <v-icon small color="#e74c3c">mdi-information-outline</v-icon>
            <span><span class="text-danger">*</span> indicates required field</span>
          </div>

          <!-- IFSC Code Section - Search -->
          <v-row class="mb-6">
            <v-col cols="12" md="6">
              <div class="form-section">
                <label class="form-label">
                  <v-icon x-small class="label-icon">mdi-code-string</v-icon>
                  IFSC Code <span class="text-danger">*</span>
                </label>
                <v-text-field
                  v-model="IfscCode"
                  :rules="IfscCodeRules"
                  placeholder="Enter IFSC Code (e.g., SBIN0001234)"
                  required
                  outlined
                  dense
                  class="mt-2"
                  clearable
                ></v-text-field>
                <v-btn
                  :disabled="!valid1"
                  @click.prevent="searchForm"
                  :loading="SearchFlag"
                  block
                  class="search-btn mt-3"
                >
                  <v-icon left x-small>mdi-magnify</v-icon>
                  Search Bank Details
                </v-btn>
              </div>
            </v-col>

            <!-- Bank Details Display -->
            <v-col cols="12" md="6">
              <v-skeleton-loader
                v-if="SearchFlag"
                class="mt-8"
                type="sentences"
              ></v-skeleton-loader>
              <div v-else-if="FormFlag" class="bank-details-display">
                <div class="detail-item">
                  <div class="detail-icon">
                    <v-icon small>mdi-bank-outline</v-icon>
                  </div>
                  <div>
                    <span class="detail-label">Bank Name</span>
                    <div class="detail-value">{{ BankName }}</div>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">
                    <v-icon small>mdi-map-marker-outline</v-icon>
                  </div>
                  <div>
                    <span class="detail-label">Branch Name</span>
                    <div class="detail-value">{{ BranchName }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="bank-details-placeholder">
                <v-icon size="48" color="#EDBE38">mdi-bank</v-icon>
                <p>Enter IFSC code and search to get bank details</p>
              </div>
            </v-col>
          </v-row>

          <!-- Account Details Form -->
          <template v-if="FormFlag">
            <!-- Account Holder Details -->
            <div class="form-group-title">
              <v-icon small>mdi-account-multiple</v-icon>
              <span>Account Holder Details</span>
            </div>

            <v-row class="mb-6">
              <v-col cols="12" md="6">
                <div class="form-section">
                  <label class="form-label">
                    <v-icon x-small class="label-icon">mdi-account</v-icon>
                    Account Name <span class="text-danger">*</span>
                  </label>
                  <v-text-field
                    v-model="Name"
                    :rules="NameRules"
                    placeholder="Account holder name"
                    required
                    outlined
                    dense
                    class="mt-2"
                    clearable
                  ></v-text-field>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="form-section">
                  <label class="form-label">
                    <v-icon x-small class="label-icon">mdi-credit-card</v-icon>
                    Account Number <span class="text-danger">*</span>
                  </label>
                  <v-text-field
                    v-model="AccountNumber"
                    :rules="AccountNumberRules"
                    :counter="15"
                    :type="show3 ? 'text' : 'password'"
                    placeholder="Enter account number"
                    required
                    outlined
                    dense
                    class="mt-2"
                    clearable
                  ></v-text-field>
                </div>
              </v-col>
            </v-row>

            <!-- Confirm Account Number -->
            <v-row class="mb-6">
              <v-col cols="12" md="6">
                <div class="form-section">
                  <label class="form-label">
                    <v-icon x-small class="label-icon">mdi-credit-card-check</v-icon>
                    Confirm Account Number <span class="text-danger">*</span>
                  </label>
                  <v-text-field
                    v-model="ReEnterAccountNumber"
                    :rules="ReEnterAccountNumberRules"
                    :counter="15"
                    placeholder="Re-enter account number"
                    required
                    outlined
                    dense
                    class="mt-2"
                    clearable
                  ></v-text-field>
                </div>
              </v-col>
            </v-row>

            <!-- Document Upload Section -->
            <div class="form-group-title mt-6">
              <v-icon small>mdi-file-document</v-icon>
              <span>Document Upload</span>
            </div>

            <v-row class="mb-6">
              <v-col cols="12" md="6">
                <div class="form-section">
                  <label class="form-label">
                    <v-icon x-small class="label-icon">mdi-image-plus</v-icon>
                    Bank Document <span class="text-danger">*</span>
                  </label>
                  <small class="file-help-text">JPG, PNG, JPEG accepted (Max 5MB)</small>
                  <v-file-input
                    v-model="UploadedImage"
                    :rules="ImageRules"
                    :clearable="true"
                    truncate-length="25"
                    accept="image/png, image/jpeg, image/jpg"
                    placeholder="Click to upload bank document"
                    prepend-icon="mdi-image"
                    @change="addImage(1)"
                    outlined
                    dense
                    class="mt-2"
                  ></v-file-input>
                </div>
              </v-col>

              <!-- Image Preview -->
              <v-col cols="12" md="6" v-if="SelectedImage.ImageUrl">
                <div class="image-preview-section">
                  <label class="form-label">
                    <v-icon x-small>mdi-image-multiple</v-icon>
                    Document Preview
                  </label>
                  <img :src="SelectedImage.ImageUrl" class="preview-image" />
                </div>
              </v-col>
            </v-row>

            <!-- Image Editor Section -->
            <v-row v-if="SelectedImage.ImageUrl" class="mb-6">
              <v-col cols="12">
                <div class="image-editor-section">
                  <div class="editor-header">
                    <v-icon small>mdi-image-edit</v-icon>
                    <span>Edit Document Image</span>
                  </div>

                  <div class="editor-controls">
                    <v-btn
                      small
                      @click.prevent="flip(true, false)"
                      class="editor-btn"
                      title="Flip Horizontal"
                    >
                      <v-icon small>mdi-swap-horizontal-bold</v-icon>
                    </v-btn>
                    <v-btn
                      small
                      @click.prevent="flip(false, true)"
                      class="editor-btn"
                      title="Flip Vertical"
                    >
                      <v-icon small>mdi-swap-vertical-bold</v-icon>
                    </v-btn>
                    <v-btn
                      small
                      @click.prevent="rotate(90)"
                      class="editor-btn"
                      title="Rotate Right"
                    >
                      <v-icon small>mdi-rotate-right-variant</v-icon>
                    </v-btn>
                    <v-btn
                      small
                      @click.prevent="rotate(-90)"
                      class="editor-btn"
                      title="Rotate Left"
                    >
                      <v-icon small>mdi-rotate-left-variant</v-icon>
                    </v-btn>
                  </div>

                  <div class="cropper-container">
                    <cropper ref="cropper" :src="SelectedImage.ImageUrl" />
                  </div>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>

        <!-- Action Buttons -->
        <v-card-actions class="form-actions">
          <v-row class="w-100 ma-0">
            <v-col cols="12" sm="6" class="d-flex gap-2">
              <v-btn
                :disabled="!valid1"
                @click.prevent="confirmAlert()"
                :loading="SubmitFlag"
                class="submit-btn flex-grow-1"
              >
                <v-icon left small>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6" class="d-flex">
              <v-btn
                @click="closePrompt"
                class="cancel-btn flex-grow-1"
              >
                <v-icon left small>mdi-close</v-icon>
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-form>
    </v-card>
  </div>
</template>
  
<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  data() {
    return {
      valid1: true,
      LoadingFlag: false,
      SubmitFlag: false,
      show3: false,
      SearchFlag: false,

      FormFlag: false,
      NameRules: [(v) => !!v || "Name is required"],
      Name: "",
      AccountNumber: "",
      AccountNumber: "",
      ReEnterAccountNumber: "",

      AccountNumberRules: [
        (v) => !!v || "Account Number is required",
        (v) => v.length <= 15 || "Account Number must be 15 characters",
      ],

      ReEnterAccountNumberRules: [
        (v) => !!v || "Re-Enter Account Number is required",
        (v) => v <= this.AccountNumber || "Not match.",
      ],
      IfscCodeRules: [(v) => !!v || "IFSC Code is required"],
      IfscCode: "",

      ImageRules: [(v) => !!v || "Image is required"],
      SelectedImage: {},
      UploadedImage: {},

      BankName: "",
      BranchName: "",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      CurrentYearId: "",
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },

    IfscCode: function () {
      console.log("watch IfscCode");
      this.FormFlag = false;
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "jci_india_lom",
        Action: "edit_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
    },
    addImage(id) {
      console.log("addImage called");
      console.log({ id });
      switch (id) {
        case 1:
          var file = this.UploadedImage;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.SelectedImage.src) {
            URL.revokeObjectURL(this.SelectedImage.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.SelectedImage = {
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        default:
          break;
      }
    },
    deleteImage(id) {
      console.log("deleteImage called");
      console.log({ id });
      switch (id) {
        case 1:
          this.SelectedImage = {
            ImageType: null,
            ImageUrl: null,
            UploadedImage: null,
          };
          this.UploadedImage = {};
          break;

        default:
          break;
      }
    },
    cropImage() {
      // get image data for post processing, e.g. upload or setting image src
      this.cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL();
    },
    flip(x, y) {
      console.log("flip x=" + x + ", y=" + y);
      this.$refs.cropper.flip(x, y);
    },
    rotate(angle) {
      console.log("rotate=" + angle);
      this.$refs.cropper.rotate(angle);
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideupdatePrompt");
    },
    searchForm() {
      console.log("searchForm called");
      this.SearchFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/get-bank-details";
      var upload = {
        IfscCode: this.IfscCode,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          thisIns.SearchFlag = false;
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.SubmitFlag = false;

          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          if (flag == 1) {
            thisIns.BankName = records.BankName;
            thisIns.BranchName = records.BranchName;
            console.log("BankName" + thisIns.BankName);
            console.log("BranchName" + thisIns.BranchName);

            thisIns.FormFlag = true;
            console.log("FormFlag" + thisIns.FormFlag);
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.SubmitFlag = false;
        });
    },
    confirmAlert() {
      console.log("confirmAlert called");
      var validate1 = this.$refs.form2.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        var htmlTxt = "";
        // htmlTxt += "<b>Please verify all the information before proceeding</b>";
        htmlTxt += "<b>Bank Account details</b>";
        htmlTxt += "<br/><br/>";
        htmlTxt += "Account  Name : <b>" + this.Name + "</b><br/>";
        htmlTxt += "Bank Name : <b>" + this.BankName + "</b><br/>";
        htmlTxt += "Branch Name : <b>" + this.BranchName + "</b><br/>";
        htmlTxt += "Account Number : <b>" + this.AccountNumber + "</b><br/>";
        htmlTxt += "IFSC Number : <b> " + this.IfscCode + "</b><br/>";
        htmlTxt +=
          "<br/><img src='" +
          this.SelectedImage.ImageUrl +
          "' width=80 height=80/></b><br/>";
        htmlTxt += "<br/><br/>";
        htmlTxt += "Do you want to continue?";
        Swal.fire({
          title: "Are you sure?",
          text: "",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.uploadImage();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        // this.toast("error", "Kindly fill the required fields", true);
      }
    },
    uploadImage() {
      console.log("uploadImage called");
      var validate1 = this.$refs.form2.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        const upload = new FormData();
        upload.append("UserInterface", 2);
        upload.append("Name", this.Name);
        upload.append("AccountNumber", this.AccountNumber);
        upload.append("ReEnterAccountNumber", this.ReEnterAccountNumber);
        upload.append("IfscCode", this.IfscCode);

        var UploadedImageName = this.UploadedImage.name;
        UploadedImageName =
          UploadedImageName == (null || undefined) ? "" : UploadedImageName;
        if (UploadedImageName != "") {
          const { canvas } = this.$refs.cropper.getResult();
          if (canvas) {
            canvas.toBlob((blob) => {
              upload.append("Image", blob, UploadedImageName);
              this.UploadFlag = true;
              this.submitForm(upload);
            }, "image/jpeg");
          } else {
            this.submitForm(upload);
          }
        } else {
          this.submitForm(upload);
        }
      } else {
        var message = "";
        if (validate1 == false) {
          message += "Kindly fill the required fields. ";
        }
        if (MemberId == 0) {
          message += "Kindly select member. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm(upload) {
      console.log("submitForm is called");
      var UploadFlag = this.UploadFlag;
      console.log("UploadFlag=" + UploadFlag);

      if (UploadFlag) {
        this.SubmitFlag = true;
        this.ResultFlag = false;
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom-bank-details/store";

        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );
        console.log("upload=" + JSON.stringify(upload));
        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            thisIns.SubmitFlag = false;
            thisIns.UploadFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.ResultFlag = true;
              thisIns.sweetAlert("success", output, true);
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.UploadFlag = false;
          });
      } else {
        var message = "";
        if (!UploadFlag) {
          message += "File is not selected";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  mounted() {},
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
// ============================================
// COLOR PALETTE - JCI APPROVED
// ============================================
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;

$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$border-color: #e2e8f0;

// ============================================
// ANIMATIONS
// ============================================
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

// ============================================
// MAIN CONTAINER
// ============================================
#bank-update-dialog {
  // ============================================
  // HEADER
  // ============================================
  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    border-radius: 20px 20px 0 0;
    border: none;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5), 0 0 30px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      animation: float 8s ease-in-out infinite;
    }
  }

  .header-gradient {
    position: relative;
    z-index: 1;
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(15px);
    border-radius: 14px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
    animation: float 4s ease-in-out infinite;

    .icon-white {
      color: $text-white !important;
    }
  }

  .header-title-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .header-title {
    font-size: 28px;
    font-weight: 800;
    color: $text-white;
    margin: 0;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  }

  .header-subtitle {
    font-size: 14px;
    opacity: 0.95;
    color: $text-white;
    font-weight: 500;
    margin: 0;
  }

  .gap-3 {
    gap: 1rem !important;
  }

  .header-action-btn {
    background: rgba(255, 255, 255, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.3) !important;
      transform: rotate(90deg);
    }
  }

  // ============================================
  // FORM CARD
  // ============================================
  .form-card {
    border-radius: 0 0 20px 20px !important;
    border: none;
    animation: slideInUp 0.6s ease-out;
    background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at 20% 50%, rgba(58, 103, 177, 0.02) 0%, transparent 50%);
      pointer-events: none;
    }
  }

  .form-content {
    padding: 32px !important;
    position: relative;
    z-index: 1;
  }

  // ============================================
  // REQUIRED NOTICE
  // ============================================
  .required-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 151, 215, 0.08) 0%, rgba(0, 151, 215, 0.05) 100%);
    border-left: 4px solid $aqua;
    border-radius: 8px;
    margin-bottom: 28px;
    font-size: 13px;
    font-weight: 600;
    color: $text-90-black;
  }

  // ============================================
  // FORM SECTIONS
  // ============================================
  .form-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-group-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid $border-color;

    .v-icon {
      color: $aqua;
    }
  }

  .form-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    color: $text-off-black;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    .label-icon {
      color: $aqua;
      flex-shrink: 0;
    }

    .text-danger {
      color: #e74c3c;
      font-weight: 900;
    }
  }

  .file-help-text {
    display: block;
    font-size: 11px;
    color: $text-90-black;
    margin-top: 4px;
    font-weight: 500;
  }

  // ============================================
  // BANK DETAILS DISPLAY
  // ============================================
  .bank-details-display {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, rgba(86, 189, 163, 0.08) 0%, rgba(86, 189, 163, 0.04) 100%);
    border-left: 4px solid $seaform;
    border-radius: 10px;
    animation: slideInUp 0.6s ease-out;
  }

  .detail-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .detail-icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: rgba(86, 189, 163, 0.15);
      border-radius: 8px;
      color: $seaform;
      margin-top: 2px;
    }

    .detail-label {
      display: block;
      font-size: 11px;
      font-weight: 700;
      color: $text-90-black;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }

    .detail-value {
      font-size: 14px;
      font-weight: 600;
      color: $text-off-black;
    }
  }

  .bank-details-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 32px 16px;
    background: linear-gradient(135deg, rgba(237, 190, 56, 0.05) 0%, rgba(237, 190, 56, 0.02) 100%);
    border: 2px dashed rgba(237, 190, 56, 0.3);
    border-radius: 10px;

    p {
      font-size: 12px;
      color: $text-90-black;
      margin: 0;
      text-align: center;
    }
  }

  // ============================================
  // IMAGE UPLOAD & PREVIEW
  // ============================================
  .image-preview-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .preview-image {
      width: 100%;
      max-height: 200px;
      border-radius: 10px;
      border: 2px solid $border-color;
      object-fit: cover;
    }
  }

  // ============================================
  // IMAGE EDITOR
  // ============================================
  .image-editor-section {
    padding: 20px;
    background: linear-gradient(135deg, rgba(237, 190, 56, 0.08) 0%, rgba(237, 190, 56, 0.03) 100%);
    border: 2px solid rgba(237, 190, 56, 0.2);
    border-radius: 12px;
    animation: slideInUp 0.6s ease-out;
  }

  .editor-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: $text-off-black;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid rgba(237, 190, 56, 0.3);
  }

  .editor-controls {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .editor-btn {
    background: linear-gradient(135deg, $gold 0%, darken($gold, 10%) 100%) !important;
    color: white !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 12px rgba(237, 190, 56, 0.2);

    &:hover {
      box-shadow: 0 6px 16px rgba(237, 190, 56, 0.3);
      transform: translateY(-2px);
    }
  }

  .cropper-container {
    display: flex;
    justify-content: center;
    padding: 16px;
    background: $light-bg;
    border-radius: 10px;
    border: 1px solid $border-color;
  }

  // ============================================
  // FORM ACTIONS
  // ============================================
  .form-actions {
    padding: 24px 32px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-top: 2px solid $border-color;
    position: relative;
    z-index: 1;

    .w-100 {
      width: 100%;
    }

    .ma-0 {
      margin: 0;
    }

    .gap-2 {
      gap: 12px;
    }

    .flex-grow-1 {
      flex-grow: 1;
    }
  }

  .submit-btn {
    background: linear-gradient(135deg, $aqua 0%, darken($aqua, 10%) 100%) !important;
    color: white !important;
    font-weight: 700 !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 8px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 6px 20px rgba(0, 151, 215, 0.2);

    &:hover:not(:disabled) {
      box-shadow: 0 8px 28px rgba(0, 151, 215, 0.35);
      transform: translateY(-3px);
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  .cancel-btn {
    background: $border-color !important;
    color: $text-off-black !important;
    font-weight: 700 !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    &:hover {
      background: #d0d8e0 !important;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    .header-title {
      font-size: 22px !important;
    }

    .header-subtitle {
      font-size: 12px !important;
    }

    .form-content {
      padding: 24px !important;
    }

    .form-actions {
      padding: 16px 24px !important;
    }
  }

  @media (max-width: 600px) {
    .header-title {
      font-size: 18px !important;
    }

    .header-subtitle {
      font-size: 11px !important;
    }

    .header-action-btn {
      width: 36px !important;
      height: 36px !important;
    }

    .form-content {
      padding: 16px !important;
    }

    .image-editor-section {
      padding: 12px;
    }

    .form-actions {
      padding: 12px 16px !important;

      .gap-2 {
        gap: 8px;
      }
    }

    .submit-btn,
    .cancel-btn {
      font-size: 12px !important;
    }
  }
}
</style>
